import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, Download, ExternalLink, ShieldCheck, ShieldX } from 'lucide-react';
import { Card, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { courseService } from '../../services/course.service';
import { apiClient } from '../../lib/api-client';
import type { Certificate } from '@edytech/shared/types';

// Trigger a download of the certificate PDF (authenticated endpoint returns it directly)
const downloadPdf = async (cert: Certificate) => {
  const response = await apiClient.get(`/certificates/${cert.id}/pdf`, { responseType: 'blob' });
  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${cert.courseTitle.replace(/\s+/g, '-')}-certificate.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const CertificatesPage: React.FC = () => {
  const { data: certs, isLoading } = useQuery<Certificate[]>({
    queryKey: ['my-certificates'],
    queryFn: () => courseService.getCertificates(),
  });

  const handleDownload = async (cert: Certificate) => {
    try {
      await downloadPdf(cert);
    } catch {
      alert('Failed to download certificate. Please try again.');
    }
  };

  if (isLoading) {
    return <div className="py-16 text-center text-[rgb(var(--light-secondary))]">Loading certificates...</div>;
  }

  if (!certs?.length) {
    return (
      <div>
        <h1 className="text-2xl font-bold">My Certificates</h1>
        <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">Complete a course to earn your certificate.</p>
        <div className="mt-8 rounded-xl border border-dashed border-[rgb(var(--border))] p-12 text-center text-[rgb(var(--light-secondary))]">
          <Award className="mx-auto h-12 w-12 opacity-40" />
          <p className="mt-3">No certificates yet. Finish a course to earn one!</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">My Certificates</h1>
      <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
        View and download your certificates with QR verification.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        {certs.map((cert) => {
          const isRevoked = !!cert.revokedAt;
          return (
            <Card key={cert.id} className="flex flex-col">
              <div className="flex items-start justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${isRevoked ? 'bg-amber-500/10 text-amber-500' : 'bg-primary/10 text-primary'}`}>
                  <Award className="h-6 w-6" />
                </div>
                {isRevoked ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600"
                    title={cert.revokedReason ?? undefined}
                  >
                    <ShieldX className="h-3.5 w-3.5" /> Revoked
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-600">
                    <ShieldCheck className="h-3.5 w-3.5" /> Verified
                  </span>
                )}
              </div>
              <CardTitle className="mt-4">{cert.courseTitle}</CardTitle>
              <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">Issued to {cert.fullName}</p>
              <p className="mt-2 text-xs text-[rgb(var(--light-secondary))]">
                Issued: {new Date(cert.issuedAt).toLocaleDateString()}
                {cert.revokedAt ? ` · Revoked: ${new Date(cert.revokedAt).toLocaleDateString()}` : ''}
              </p>
              <p className="mt-1 font-mono text-xs text-[rgb(var(--light-secondary))]">{cert.serialNumber}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {!isRevoked && (
                  <Button size="sm" onClick={() => handleDownload(cert)}>
                    <Download className="mr-2 h-4 w-4" /> Download PDF
                  </Button>
                )}
                {cert.verifyUrl && (
                  <a
                    href={cert.verifyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-lg border border-[rgb(var(--border))] px-3 py-1.5 text-sm font-semibold text-[rgb(var(--foreground))] transition-colors hover:bg-[rgb(var(--border))]"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" /> Verify
                  </a>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};